import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../../components/recharts/functions";
import { ReMixChart } from "../../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../../models/chart_model";
import { renderHourTick } from "../../../../../components/recharts/components";
import { H_CHART_TYPES } from "../../../const";
import {
  getBessSurplusDeficitSummaryTab,
  getSurplusOptimizerSummaryChart,
} from "../../../../../Rest_api/RestApi";
import loader from "../../../../../components/recharts/loader.gif";
import { getMonthFromDate } from "../../../../../extensions/date";
import ToggleTabs from "../../utility/ChartTableToggle";

const DataTabs = ["slot", "Date", "Month", "Summary"];
const AggregationOptions = ["Min", "Max", "Avg", "Mode"];

interface SummaryData {
  date: string;
  slot: string;
  slot_number?: number;
  slot_id: number;
  actual_total_base: number | string | null;
  demand_mw_base: number | string | null;
  surplus_deficit_mw_base: number | string | null;
  actual_total_active: number | string | null;
  demand_mw_active: number | string | null;
  surplus_deficit_mw_active: number | string | null;

  // Summary Fields
  mode_surplus_deficit_mw?: number | string | null;
  min_surplus_deficit_mw?: number | string | null;
  max_surplus_deficit_mw?: number | string | null;
  avg_surplus_deficit_mw?: number | string | null;

  month?: string;
}

const Intervals = [
  {
    label: "Time slot",
    key: "time",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
  {
    label: "Summary",
    key: "summary",
    values: ["slot_id"],
    isTime: false,
  },
];

export default function SimulationSurplusAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  setUniqueTargetDate,
  selectedTargetDate,
  sessionId,
  scenarioId,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  setUniqueTargetDate?: any;
  selectedTargetDate: any[];
  sessionId?: number;
  scenarioId?: number;
}) {
  const [data, setData] = useState<SummaryData[]>([]);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>(DataTabs[0]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);

  // Default to Mode only
  const [selectedAggregations, setSelectedAggregations] = useState<string[]>(["Mode"]);

  const chartSuffixes = [
    {
      suffix: "surplus_deficit_mw_base",
      displayName: "Base Model Surplus/Deficit",
      stackSuffix: "surplus_deficit_mw_base",
      stroke: "rgba(99, 159, 203, 1)",
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "surplus_deficit_mw_active",
      displayName: "Active Model Surplus/Deficit",
      stackSuffix: "surplus_deficit_mw_active",
      stroke: "rgba(244, 127, 89, 1)",
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
    },

    // --- Summary Metrics ---
    // Mode
    {
      suffix: "mode_surplus_deficit_mw",
      displayName: "Surplus/Deficit (Mode)",
      stackSuffix: "mode_surplus_deficit_mw",
      stroke: "#639FCB", // Original Blue
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
      aggregation: "Mode"
    },
    // Min
    {
      suffix: "min_surplus_deficit_mw",
      displayName: "Surplus/Deficit (Min)",
      stackSuffix: "min_surplus_deficit_mw",
      stroke: "#4DD0E1", // Cyan
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
      aggregation: "Min"
    },
    // Max
    {
      suffix: "max_surplus_deficit_mw",
      displayName: "Surplus/Deficit (Max)",
      stackSuffix: "max_surplus_deficit_mw",
      stroke: "#1565C0", // Dark Blue
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
      aggregation: "Max"
    },
    // Avg
    {
      suffix: "avg_surplus_deficit_mw",
      displayName: "Surplus/Deficit (Avg)",
      stackSuffix: "avg_surplus_deficit_mw",
      stroke: "#9575CD", // Purple
      chartType: ChartType.Line,
      unit: "MW",
      yAxisId: "left",
      aggregation: "Avg"
    },
  ];

  const [selectedMetrics] = useState<any[]>(chartSuffixes);
  const [selectedChartType] = useState(H_CHART_TYPES[1]);

  // Sync activeTab with selectedInterval
  useEffect(() => {
    const tabIndex = DataTabs.indexOf(activeTab);
    if (tabIndex !== -1) {
      setSelectedInterval(Intervals[tabIndex]);
    }
  }, [activeTab]);

  const handleFetchData = useCallback(async () => {
    try {
      setLoading(true);
      let response;

      if (activeTab === "Summary") {
        // Call the Summary API with proper parameters
        const params: any = {};

        // Add session_id and scenario_id if available
        if (sessionId) params.session_id = sessionId;
        if (scenarioId !== undefined && scenarioId !== null) params.scenario_id = scenarioId;

        // Add date filter if available
        if (selectedTargetDate && selectedTargetDate.length > 0) {
          const startDate = selectedTargetDate[0]?.value?.split("-").reverse().join("-") || selectedTargetDate[0];
          if (startDate) params.start_date = startDate;

          if (selectedTargetDate.length > 1) {
            const endDate = selectedTargetDate[selectedTargetDate.length - 1]?.value?.split("-").reverse().join("-") || selectedTargetDate[selectedTargetDate.length - 1];
            if (endDate) params.end_date = endDate;
          }
        }


        const res = await getBessSurplusDeficitSummaryTab(params);

        // Handle response - could be res.data or res directly
        response = res?.data || res;


      } else {
        // Call the regular API for slot/date/month tabs
        const res = await getSurplusOptimizerSummaryChart();
        response = res?.data || res;

      }

      // Handle the response
      if (Array.isArray(response)) {
        // Update unique dates for non-Summary tabs
        if (activeTab !== "Summary") {
          setUniqueTargetDate(
            Array.from(
              new Set(
                response.map((item: { date: any }) =>
                  item.date.split("-").reverse().join("-")
                )
              )
            )
          );
        }
        setData(response);
      } else if (response && typeof response === 'object' && response.data && Array.isArray(response.data)) {
        // Handle nested data structure
        const dataArray = response.data;
        if (activeTab !== "Summary") {
          setUniqueTargetDate(
            Array.from(
              new Set(
                dataArray.map((item: { date: any }) =>
                  item.date.split("-").reverse().join("-")
                )
              )
            )
          );
        }
        setData(dataArray);
      } else {
        console.error("Response is not in expected format:", response);
        setError?.("Invalid data format received from API.");
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching data", error);
      setError?.("An error occurred while fetching data.");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, sessionId, scenarioId, selectedTargetDate, setError, setUniqueTargetDate]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      return "slot_id";
    }
    if (selectedInterval.key === "summary") {
      return "slot_id";
    }
    return selectedInterval.key;
  }, [selectedInterval]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  // Process and filter data based on selected interval
  useEffect(() => {
    if (data.length === 0) {

    }

    let filteredData = data.map((item) => ({
      ...item,
      month: item.date ? format(new Date(item.date), "MMM-yy") : "",
      // Convert string numbers to actual numbers - handle null/undefined
      actual_total_base: item.actual_total_base != null ? parseFloat(String(item.actual_total_base)) : 0,
      demand_mw_base: item.demand_mw_base != null ? parseFloat(String(item.demand_mw_base)) : 0,
      surplus_deficit_mw_base: item.surplus_deficit_mw_base != null ? parseFloat(String(item.surplus_deficit_mw_base)) : 0,
      actual_total_active: item.actual_total_active != null ? parseFloat(String(item.actual_total_active)) : 0,
      demand_mw_active: item.demand_mw_active != null ? parseFloat(String(item.demand_mw_active)) : 0,
      surplus_deficit_mw_active: item.surplus_deficit_mw_active != null ? parseFloat(String(item.surplus_deficit_mw_active)) : 0,

      mode_surplus_deficit_mw: item.mode_surplus_deficit_mw != null ? parseFloat(String(item.mode_surplus_deficit_mw)) : 0,
      min_surplus_deficit_mw: item.min_surplus_deficit_mw != null ? parseFloat(String(item.min_surplus_deficit_mw)) : 0,
      max_surplus_deficit_mw: item.max_surplus_deficit_mw != null ? parseFloat(String(item.max_surplus_deficit_mw)) : 0,
      avg_surplus_deficit_mw: item.avg_surplus_deficit_mw != null ? parseFloat(String(item.avg_surplus_deficit_mw)) : 0,
    }));

    if (selectedTargetDate.length > 0 && activeTab !== "Summary") {
      const types = selectedTargetDate.map(
        (t: any) => t.value?.split("-").reverse().join("-") ?? t
      );
      filteredData = filteredData.filter((row) => types.includes(row.date));
    }

    function getKeys() {
      if (selectedInterval.key === "time") {
        return ["slot_id", "date"];
      } else if (selectedInterval.key === "date") {
        return ["date"];
      }
      return [selectedInterval.values[0]];
    }

    function getTableKeys() {
      if (selectedInterval.key === "summary") {
        return ["slot_id"];
      }
      return ["slot_id", "date", "month"];
    }

    // Determine aggregation keys based on activeTab and selections
    let aggregationKeys: string[] = [];
    if (activeTab === "Summary") {
      if (selectedAggregations.includes("Mode")) aggregationKeys.push("mode_surplus_deficit_mw");
      if (selectedAggregations.includes("Min")) aggregationKeys.push("min_surplus_deficit_mw");
      if (selectedAggregations.includes("Max")) aggregationKeys.push("max_surplus_deficit_mw");
      if (selectedAggregations.includes("Avg")) aggregationKeys.push("avg_surplus_deficit_mw");
    } else {
      aggregationKeys = [
        "surplus_deficit_mw_base",
        "surplus_deficit_mw_active"
      ];
    }

    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: getTableKeys(),
      groupBy: [""],
      volumeKey: "",
      aggregationKeys: aggregationKeys,
      averageKeys: aggregationKeys,
      averageDuplicationKeys: getTableKeys(),
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);


    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [data, selectedInterval, selectedTargetDate, activeTab, selectedAggregations]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    stroke: string,
    legendColor: string,
    yAxisId: string
  ) => {
    const dataKey = key ? `${key}_${suffix}` : suffix;
    return {
      dataKey: dataKey,
      name: displayName,
      legendColor: legendColor || stroke,
      type: ChartType.Line,
      unit: unit,
      stroke: stroke,
      yAxisId: yAxisId,
      stackId: suffix.includes("surplus") ? "stack1" : undefined,
    };
  };

  function getLegends() {
    let relevantSuffixes: string[] = [];

    if (activeTab === "Summary") {
      if (selectedAggregations.includes("Mode")) relevantSuffixes.push("mode_surplus_deficit_mw");
      if (selectedAggregations.includes("Min")) relevantSuffixes.push("min_surplus_deficit_mw");
      if (selectedAggregations.includes("Max")) relevantSuffixes.push("max_surplus_deficit_mw");
      if (selectedAggregations.includes("Avg")) relevantSuffixes.push("avg_surplus_deficit_mw");
    } else {
      relevantSuffixes = ["surplus_deficit_mw_base", "surplus_deficit_mw_active"];
    }

    const legends = selectedMetrics
      .filter((m) => relevantSuffixes.includes(m.suffix))
      .flatMap(
        ({
          suffix,
          displayName,
          stackSuffix,
          chartType,
          unit,
          yAxisId,
          stroke,
          legendColor,
        }) =>
          chartKeys.length > 0
            ? chartKeys.map((key, index) =>
              createChartObject(
                key,
                index,
                suffix,
                displayName,
                stackSuffix,
                chartType,
                unit,
                stroke,
                legendColor,
                yAxisId
              )
            )
            : [createChartObject(
              "",
              0,
              suffix,
              displayName,
              stackSuffix,
              chartType,
              unit,
              stroke,
              legendColor,
              yAxisId
            )]
      );


    return legends;
  }

  function secondDataKey() {
    if (selectedInterval.isTime && selectedBlock.length > 0) {
      return "date";
    }
    if (selectedInterval.isTime) {
      return "date";
    }
    return undefined;
  }

  const handleAggregationToggle = (agg: string) => {
    setSelectedAggregations(prev => {
      // If the user tries to deselect the only remaining option, do nothing
      if (prev.includes(agg) && prev.length === 1) {
        return prev;
      }

      if (prev.includes(agg)) {
        return prev.filter(x => x !== agg);
      } else {
        return [...prev, agg];
      }
    });
  };

  return (
    <div className="dash-card col-span-1 md:col-span-12 border border-[#718EBF1C] !bg-[#fff] !p-2 !min-h-[100%] w-full">
      <div className="flex justify-between items-center mb-0">
        <h2 className="chartHeading !text-sm w-60">Surplus-Deficit Balance</h2>

        {/* Aggregation Filters - Only shown for Summary Tab */}
        {activeTab === "Summary" && (
          <div className="flex items-center gap-2">
            {AggregationOptions.map((opt) => (
              <label key={opt} className="flex items-center gap-1.5 cursor-pointer select-none">
                <div className={`w-3 h-3 rounded border flex items-center justify-center transition-colors ${selectedAggregations.includes(opt)
                  ? "bg-[#34656D] border-[#34656D]"
                  : "border-gray-300"
                  }`}>
                  {selectedAggregations.includes(opt) && (
                    <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <input
                  type="checkbox"
                  className="hidden"
                  checked={selectedAggregations.includes(opt)}
                  onChange={() => handleAggregationToggle(opt)}
                />
                <span className="text-xs text-[#525062] font-medium">{opt}</span>
              </label>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <ToggleTabs
            tabs={DataTabs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </div>
      </div>
      {loading ? (
        <div className="w-full bg-white flex items-center justify-center">
          <img
            src={loader}
            width={200}
            alt="loader"
            className="block mt-[5rem]"
          />
        </div>
      ) : (
        <ReMixChart
          chartHeight="calc(100% - 50px)"
          data={chartData}
          showBrush={chartData.length > 96 * 1 ? true : undefined}
          brushStart={BrushStart.Start}
          isTimeSlot={selectedInterval.isTime}
          onlyTitle={true}
          legends={getLegends()}
          yAxisLabel="MW"
          nameGap={40}
          xDataKey={xDataKey}
          top={10}
          bottom={30}
          xTick={
            selectedInterval.isTime && selectedBlock.length === 0
              ? (value: any, index: any) => renderHourTick(value, 10)
              : undefined
          }
          secondXDataKey={secondDataKey()}
          showToolbox={false}
        />
      )}
    </div>
  );
}